import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import {
  TChatbotClarificationContext,
  TChatbotConversationHistory,
  TRESPONSE_AI_ANALYTICS_QUESTION,
} from '@monorepo/types';
import {
  ConfirmationModalComponent,
  DefaultInfoModalComponent,
  MOBILE_SCREEN_UTILS,
  TConfirmationModalDimissEmitVal,
} from '@monorepo/ui';
import { ModalController } from '@ionic/angular';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, take } from 'rxjs';
import {
  AI_ANALYTICS_CHAT_UI_MODEL,
  TChatUiComponentUpdateEvent,
  TChatUiModelFeedback,
  TChatUiQuery,
  TChatUiTableAndChartResponseData,
} from '@monorepo/ui/chat-ui';
import {
  AI_ANALYTICS_QUICK_QUESTIONS,
  MAX_QUERIES_ALLOWED_IN_AI_ANALYTICS_CHAT,
  START_NEW_CONVERSATION_CONFIRMATION_TEXT,
} from '../../state/ai-analytics/ai-analytics.models';
import { AiAnalyticsFacade } from '../../state/ai-analytics/ai-analytics.facade';
import {
  TableGraphViewModalComponent,
  TTableGraphViewModalInputs,
} from '../table-graph-view-modal/table-graph-view-modal.component';
import { ExcelUtilClass, optionalProperty } from '@monorepo/utils';
import { select, Store } from '@ngrx/store';
import { IAppState, selectedFactoryNode, selectHeaderInfo } from '../../state';
import { TUserLoggedInInfo, UnifiedLoginService } from '@monorepo/services';

@Component({
  selector: 'monorepo-ai-analytics-modal',
  templateUrl: './ai-analytics-modal.component.html',
  styleUrls: ['./ai-analytics-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAnalyticsModalComponent implements OnInit {
  readonly #getChatUiModel$ = this.aiAnalyticsFacade.getChatUiModel();
  readonly quickQuestions = AI_ANALYTICS_QUICK_QUESTIONS;
  readonly #successQueryCount = signal(0);
  readonly #conversation = signal<{
    history: TChatbotConversationHistory;
    clarificationContext?: TChatbotClarificationContext;
  }>({ history: [] });
  readonly isNewChatBtnDisabled = signal(false);
  readonly #isMobileScreen = MOBILE_SCREEN_UTILS.isMobileScreen();
  readonly #currentFactoryId = signal<string | undefined>(undefined);
  readonly #currentSeparatorText = signal<string | undefined>(undefined);

  chatUiModel = toSignal(
    this.#getChatUiModel$.pipe(map((res) => res ?? AI_ANALYTICS_CHAT_UI_MODEL.createIdentityModel())),
    { initialValue: AI_ANALYTICS_CHAT_UI_MODEL.createIdentityModel() },
  );

  constructor(
    private aiAnalyticsFacade: AiAnalyticsFacade,
    private modalController: ModalController,
    private excelutils: ExcelUtilClass,
    private store: Store<IAppState>,
    private unifiedLoginService: UnifiedLoginService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    combineLatest([
      this.store.pipe(select(selectHeaderInfo), take(1)),
      this.store.pipe(select(selectedFactoryNode), take(1)),
      this.aiAnalyticsFacade.getLastSeparatorText().pipe(take(1)),
    ]).subscribe(([headerInfo, selectedNodeInfo, lastSeparatorText]) => {
      const { factoryId: primaryFactoryId } = this.unifiedLoginService.getUserInfo() as TUserLoggedInInfo;
      const isMmAnalytics = headerInfo.activeTab === 'MM_ANALYTICS';
      const sk: string | undefined = selectedNodeInfo?.node?.args?.['sk'];
      const currentFactoryId = isMmAnalytics && sk ? sk : primaryFactoryId;
      const separatorText =
        isMmAnalytics && selectedNodeInfo?.node?.text ? selectedNodeInfo.node.text : primaryFactoryId;

      this.#currentFactoryId.set(currentFactoryId);
      this.#currentSeparatorText.set(separatorText);

      if (separatorText !== lastSeparatorText) {
        if (this.chatUiModel().isWaiting) {
          this.#addSeparatorWhenIdle(separatorText);
        } else {
          this.aiAnalyticsFacade.setChatUiModel(
            AI_ANALYTICS_CHAT_UI_MODEL.upsertSeparator(this.chatUiModel(), separatorText),
          );
          this.aiAnalyticsFacade.setLastSeparatorText(separatorText);
        }
      }
    });
  }

  #addSeparatorWhenIdle(separatorText: string) {
    this.#getChatUiModel$
      .pipe(
        filter((model) => model !== null && !model.isWaiting),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((model) => {
        this.aiAnalyticsFacade.setChatUiModel(AI_ANALYTICS_CHAT_UI_MODEL.upsertSeparator(model!, separatorText));
        this.aiAnalyticsFacade.setLastSeparatorText(separatorText);
      });
  }

  async chatUiUpdateEvent(payload: TChatUiComponentUpdateEvent<TRESPONSE_AI_ANALYTICS_QUESTION>) {
    this.isNewChatBtnDisabled.set(true);

    switch (payload.type) {
      case 'QUERY_SEND': {
        this.aiAnalyticsFacade.setChatUiModel(payload.chatUiModel);
        await this.#askAiChatQuestion(payload.query);
        break;
      }

      case 'FEEDBACK_SUBMIT': {
        await this.#submitFeedback(payload.feedback, payload.rawData, payload.chatIndex);
        break;
      }

      case 'GENERATE_INSIGHTS': {
        this.aiAnalyticsFacade.setChatUiModel(payload.chatUiModel);
        const insightsInfo = await this.#getInsightsInfo(payload);
        this.aiAnalyticsFacade.setChatUiModel(
          AI_ANALYTICS_CHAT_UI_MODEL.updateInsigtsInfoByIndex(payload.chatUiModel, payload.chatIndex, insightsInfo),
        );
        break;
      }

      case 'VIEW_TABLE':
      case 'VIEW_CHART': {
        await this.#viewTableGraphModal(payload);
        break;
      }

      case 'DOWNLOAD_TABLE_DATA': {
        if (payload.rawData.status === 'success')
          this.excelutils.generateCsvFromData(
            payload.rawData.headers,
            payload.rawData.query_results,
            'ai-analytics-data.csv',
          );
        break;
      }

      default: {
        if (payload.chatUiModel) this.aiAnalyticsFacade.setChatUiModel(payload.chatUiModel);
        break;
      }
    }

    this.isNewChatBtnDisabled.set(false);
  }

  async #getInsightsInfo(
    payload: Extract<TChatUiComponentUpdateEvent<TRESPONSE_AI_ANALYTICS_QUESTION>, { type: 'GENERATE_INSIGHTS' }>,
  ): Promise<TChatUiTableAndChartResponseData['insightsInfo']> {
    const insights =
      payload.rawData.status === 'success'
        ? await this.aiAnalyticsFacade.generateInsights({
            original_query: payload.rawData.original_query,
            query_results: payload.rawData.query_results,
          })
        : null;

    return insights && insights.status === 'success'
      ? { status: 'GENERATED' as const, insights: insights.insights }
      : { status: 'ERROR', error: insights?.error ?? 'aiAnalytics.InsightsError' };
  }

  async #viewTableGraphModal(
    payload: Extract<
      TChatUiComponentUpdateEvent<TRESPONSE_AI_ANALYTICS_QUESTION>,
      { type: 'VIEW_TABLE' | 'VIEW_CHART' }
    >,
  ) {
    const componentInputs: TTableGraphViewModalInputs =
      payload.type === 'VIEW_TABLE'
        ? { type: 'TABLE', tableModel: payload.tableModel, data: payload.rawData }
        : { type: 'CHART', chartModel: payload.chartModel };

    const viewModel = await this.modalController.create({
      component: TableGraphViewModalComponent,
      mode: 'ios',
      componentProps: { componentInputs: structuredClone(componentInputs) },
    });
    await viewModel.present();
  }

  async #askAiChatQuestion(query: TChatUiQuery) {
    const conversation = this.#conversation();
    const factoryId = this.#currentFactoryId();

    if (this.#successQueryCount() === 0) {
      const aiChatResponse = await this.aiAnalyticsFacade.askFirstQuestion({
        query: query.value,
        ...optionalProperty('clarification_context', conversation.clarificationContext),
        ...optionalProperty('conversation_history', conversation.history),
        ...optionalProperty('factory_id', factoryId),
      });
      this.#handleAiChatResponse(query, aiChatResponse);
    } else if (this.#successQueryCount() < MAX_QUERIES_ALLOWED_IN_AI_ANALYTICS_CHAT) {
      const aiChatResponse = await this.aiAnalyticsFacade.askFollowUpQuestion({
        query: query.value,
        conversation_history: conversation.history,
        clarification_context: conversation.clarificationContext,
        ...optionalProperty('factory_id', factoryId),
      });
      this.#handleAiChatResponse(query, aiChatResponse);
    } else {
      this.aiAnalyticsFacade.setChatUiModel(
        AI_ANALYTICS_CHAT_UI_MODEL.addNewResponse(this.chatUiModel(), null, this.#isMobileScreen),
      );
      this.openCreateNewTabConfirmationModel('LIMIT_REACHED');
    }
  }

  #handleAiChatResponse(query: TChatUiQuery, aiChatResponse: TRESPONSE_AI_ANALYTICS_QUESTION | null) {
    this.aiAnalyticsFacade.setChatUiModel(
      AI_ANALYTICS_CHAT_UI_MODEL.addNewResponse(this.chatUiModel(), aiChatResponse, this.#isMobileScreen),
    );

    if (aiChatResponse) {
      switch (aiChatResponse.status) {
        case 'success': {
          this.#conversation.update((old) => ({
            history: [...old.history, { User: query.value, Assistant: aiChatResponse.natural_language_summary }],
          }));
          break;
        }

        case 'follow_up_needed':
        case 'clarification_needed': {
          this.#conversation.update((old) => ({
            ...old,
            history: [...old.history, { User: query.value, Assistant: aiChatResponse.natural_language_summary }],
            clarificationContext: aiChatResponse.clarification_context,
          }));
          break;
        }
      }
    }

    this.#successQueryCount.update((old) => (aiChatResponse?.status === 'success' ? old + 1 : old));
  }

  async openCreateNewTabConfirmationModel(reason: keyof typeof START_NEW_CONVERSATION_CONFIRMATION_TEXT) {
    const createNewTabConfirmationModal = await this.modalController.create({
      component: ConfirmationModalComponent,
      mode: 'ios',
      componentProps: {
        header: START_NEW_CONVERSATION_CONFIRMATION_TEXT[reason].header,
        contentTxts: START_NEW_CONVERSATION_CONFIRMATION_TEXT[reason].content,
        primaryBtnTxt: 'aiAnalytics.StartNewChat',
        closeModalBtnTxt: 'aiAnalytics.StartNewChatCancel',
      },
      showBackdrop: true,
      backdropDismiss: true,
      cssClass: 'ai-analytics-util-modal',
    });

    await createNewTabConfirmationModal.present();
    const dismissEmitValue = (await createNewTabConfirmationModal.onWillDismiss()) as TConfirmationModalDimissEmitVal;

    if (dismissEmitValue.role === 'SAVE') this.#resetAndCreateNewTab();
  }

  #resetAndCreateNewTab() {
    const freshModel = AI_ANALYTICS_CHAT_UI_MODEL.createIdentityModel();
    const separatorText = this.#currentSeparatorText();
    if (separatorText) {
      this.aiAnalyticsFacade.setChatUiModel(AI_ANALYTICS_CHAT_UI_MODEL.upsertSeparator(freshModel, separatorText));
      this.aiAnalyticsFacade.setLastSeparatorText(separatorText);
    } else {
      this.aiAnalyticsFacade.setChatUiModel(freshModel);
    }
    this.#successQueryCount.set(0);
    this.isNewChatBtnDisabled.set(false);
    this.#conversation.set({ history: [] });
  }

  async #submitFeedback(feedback: TChatUiModelFeedback, responseData: TRESPONSE_AI_ANALYTICS_QUESTION, index: number) {
    const isFeedbackSubmitSuccess = await this.aiAnalyticsFacade.submitFeedback({
      feedback_rating: feedback === 'POSITIVE' ? 1 : 0,
      user_input: responseData.original_query,
      generated_response: responseData.generated_sql ?? '',
    });

    if (isFeedbackSubmitSuccess) {
      const infoModal = await this.modalController.create({
        component: DefaultInfoModalComponent,
        componentProps: {
          infoHeader: 'aiAnalytics.ThankYou',
          infoTxt: 'aiAnalytics.FeedbackSuccessMsg',
          infoIcon: 'assets/img/checked.svg',
        },
        cssClass: 'ai-analytics-util-modal',
      });
      await infoModal.present();
      this.aiAnalyticsFacade.setChatUiModel(
        AI_ANALYTICS_CHAT_UI_MODEL.setFeedbackSubmitByIndex(this.chatUiModel(), index, feedback),
      );
    }
  }

  closeModal() {
    this.modalController.dismiss();
  }
}
